import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface HudShellProps {
  /** The ResourceBar. Stays pinned at the top on every screen size. */
  topBar?: ReactNode;
  /** Desktop left rail navigation. Hidden below `lg`. */
  sidebar?: ReactNode;
  /** Mobile bottom dock. Hidden from `lg` up. */
  dock?: ReactNode;
  /** The centre view — orbit canvas, building grid, sector map. */
  children: ReactNode;
  /** Right column: queue, events, fleet movements. Stacks below the main view on mobile. */
  aside?: ReactNode;
  /** Theme id — matches a `[data-sg-theme]` block in tokens.css. */
  theme?: 'dark' | 'command';
  className?: string;
}

/**
 * The application frame from the GDD §4 wireframe.
 *
 * Layout contract, and why it's built this way:
 *   • The top resource bar is always visible — resources are consulted on
 *     literally every screen, so it never scrolls away.
 *   • Navigation moves from a left rail (desktop) to a bottom dock (mobile)
 *     rather than collapsing into a hamburger. The GDD targets Capacitor
 *     builds, and a thumb-reachable dock is the native-app expectation.
 *   • The aside stacks *below* the main view on mobile, not above: the orbit
 *     canvas is the emotional anchor of the game and should be the first thing
 *     on screen after the resource bar.
 *
 * `.sg-root` carries the design system's reset and base typography — every
 * component assumes it is somewhere above them in the tree.
 */
export function HudShell({
  topBar,
  sidebar,
  dock,
  children,
  aside,
  theme = 'dark',
  className,
}: HudShellProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);

  // The sidebar sticks BELOW the top bar, and a sticky element's offset is
  // measured against the viewport rather than against its sibling — so the
  // rail needs to know the bar's real rendered height. A hardcoded guess
  // drifts the moment padding, font size or content (icons, player name
  // length) changes the bar's height, so it is measured instead and written
  // onto the root as a CSS custom property every layout the bar's size
  // actually changes.
  useLayoutEffect(() => {
    const bar = topBarRef.current;
    const root = rootRef.current;
    if (!bar || !root) return;
    const apply = () => {
      root.style.setProperty('--sg-topbar-h', `${bar.getBoundingClientRect().height}px`);
    };
    apply();
    // jsdom (unit tests) doesn't implement ResizeObserver; the static
    // fallback value above covers that environment fine.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(apply);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  // Same reasoning as the top bar, mirrored for the bottom: the dock is
  // `position: fixed`, so the spacer below the scrollable content that
  // keeps it from covering the last card has to know the dock's *real*
  // height, not a guess — NavMenu's dock variant can grow a second row
  // when its "Mehr" overflow expands, and a fixed spacer would let that
  // taller dock cover content underneath it.
  useLayoutEffect(() => {
    const el = dockRef.current;
    const root = rootRef.current;
    if (!el || !root) return;
    const apply = () => {
      root.style.setProperty('--sg-dock-h', `${el.getBoundingClientRect().height}px`);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      data-sg-theme={theme}
      className={cn(
        // `overflow-x-clip` is a frame-level guarantee: the page itself must
        // never scroll sideways on a phone. Regions that legitimately overflow
        // (the resource strip, wide tables) carry their own scroll container,
        // and clip — unlike hidden — does not create a scroll container here,
        // so `sticky` children keep working.
        'sg-root flex min-h-screen flex-col overflow-x-clip bg-surface-base',
        // Fallback for the first paint before the ResizeObserver above runs.
        '[--sg-topbar-h:3.25rem] [--sg-dock-h:3.5rem]',
        className,
      )}
    >
      <div ref={topBarRef}>{topBar}</div>

      <div className="flex min-h-0 flex-1">
        {sidebar ? (
          <aside className="hidden w-52 shrink-0 border-r border-edge bg-surface-1 lg:block">
            <div
              className={cn(
                'sticky top-[var(--sg-topbar-h,3.25rem)]',
                // Its own scroll container, capped to what is left of the
                // viewport: a long nav scrolls inside the rail instead of
                // scrolling the page and disappearing behind the top bar.
                'max-h-[calc(100vh-var(--sg-topbar-h,3.25rem))] overflow-y-auto overscroll-contain',
              )}
            >
              {sidebar}
            </div>
          </aside>
        ) : null}

        <main className="min-w-0 flex-1">
          <div
            className={cn(
              'grid gap-3 p-3 sm:gap-4 sm:p-4',
              aside ? 'grid-cols-1 xl:grid-cols-[minmax(0,1fr)_22rem]' : 'grid-cols-1',
            )}
          >
            <div className="min-w-0 space-y-3 sm:space-y-4">{children}</div>
            {aside ? <div className="min-w-0 space-y-3 sm:space-y-4">{aside}</div> : null}
          </div>

          {/* Spacer so the mobile dock never covers the last card — sized to
              the dock's real (possibly overflow-expanded) height, not a guess. */}
          {dock ? <div className="h-[var(--sg-dock-h,3.5rem)] lg:hidden" aria-hidden /> : null}
        </main>
      </div>

      {dock ? (
        <div ref={dockRef} className="fixed inset-x-0 bottom-0 lg:hidden">
          {dock}
        </div>
      ) : null}
    </div>
  );
}

export interface ViewHeaderProps {
  title: ReactNode;
  eyebrow?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

/** Section heading for a main view. */
export function ViewHeader({ title, eyebrow, description, actions, className }: ViewHeaderProps) {
  return (
    <div data-tour="view-header" className={cn('flex flex-wrap items-end justify-between gap-3', className)}>
      <div className="min-w-0">
        {eyebrow ? (
          <div className="font-display text-label-sm uppercase text-content-muted">{eyebrow}</div>
        ) : null}
        <h2 className="font-display text-base font-semibold uppercase tracking-[0.14em] text-content">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 max-w-prose text-xs leading-relaxed text-content-muted">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
